import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, Search, Edit2, Trash2, Mail, Phone, X, Building2, Tag, FileText, Users, Download, Upload, AlertCircle, Check } from 'lucide-react';
import { api } from '../lib/api';
import { Lead, UserCategory } from '../types';
import { CategoryBadge, ConfirmModal, Spinner, useToast, Toast } from '../components/UI';

export default function LeadsPage(): React.ReactElement {
  const { toast, show } = useToast();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [categories, setCategories] = useState<UserCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [modal, setModal] = useState<'add' | Lead | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Lead | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ company: '', contact: '', email: '', phone: '', category: '', notes: '' });
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([api.getLeads(), api.getCategories()]) as [Lead[], UserCategory[]];
      setLeads(l); setCategories(c);
    } catch { setLeads([]); setCategories([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm({ company: '', contact: '', email: '', phone: '', category: categories[0]?.name || '', notes: '' });
    setModal('add');
  };
  const openEdit = (l: Lead) => {
    setForm({ company: l.company, contact: l.contact || '', email: l.email, phone: l.phone, category: l.category, notes: l.notes || '' });
    setModal(l);
  };

  const save = async () => {
    if (!form.company.trim()) { show('Company name is required', 'error'); return; }
    if (!form.email.trim()) { show('Email is required', 'error'); return; }
    setSaving(true);
    try {
      if (modal === 'add') {
        const created = await api.createLead(form);
        setLeads(prev => [created, ...prev]);
        show(`"${form.company}" added`, 'success');
      } else if (modal && typeof modal === 'object') {
        const updated = await api.updateLead(modal.id, form);
        setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
        show('Lead updated', 'success');
      }
      setModal(null);
    } catch (e: any) { show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.deleteLead(deleteTarget.id).catch(() => null);
    setLeads(prev => prev.filter(l => l.id !== deleteTarget.id));
    show('Lead deleted', 'info'); setDeleteTarget(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try { await api.exportLeads(); show('Download started', 'success'); }
    catch (e: any) { show(e.message, 'error'); }
    finally { setExporting(false); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    try {
      const result = await api.importLeads(file);
      setImportResult(result);
      await load(); // Refresh leads list
    } catch (e: any) { show(e.message, 'error'); }
    finally { setImporting(false); }
  };

  const fld = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));

  const getCatColor = (n: string) => categories.find(c => c.name === n)?.color;

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q || l.company.toLowerCase().includes(q) || l.email.toLowerCase().includes(q) || (l.contact || '').toLowerCase().includes(q);
    const matchCat = catFilter === 'All' || l.category === catFilter;
    return matchSearch && matchCat;
  });

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      <div className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-sub">{leads.length} lead{leads.length !== 1 ? 's' : ''} in your pipeline</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={handleExport} disabled={exporting}>
            {exporting ? <Spinner size={14} /> : <Download size={14} />} Export Excel
          </button>
          <button className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={importing}>
            {importing ? <Spinner size={14} /> : <Upload size={14} />} Import
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleImport} />
          <button className="btn btn-primary" onClick={openAdd}><Plus size={15} /> Add Lead</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-wrap" style={{ maxWidth: 300 }}>
          <Search size={14} />
          <input placeholder="Search leads…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['All', ...categories.map(c => c.name)].map(cat => (
            <button key={cat} className={`chip ${catFilter === cat ? 'active' : ''}`} onClick={() => setCatFilter(cat)}>{cat}</button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><Spinner size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Users size={32} />
            <h3>{search || catFilter !== 'All' ? 'No results found' : 'No leads yet'}</h3>
            <p>{search || catFilter !== 'All' ? 'Try adjusting your search or filter.' : 'Add your first lead to get started.'}</p>
            {!search && catFilter === 'All' && <button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> Add Lead</button>}
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 8 }}>
          {filtered.map((lead, i) => (
            <div key={lead.id} className="list-row" style={{ borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none', borderRadius: 0 }}>
              <div className="avatar" style={{ background: `${getCatColor(lead.category) || 'var(--accent)'}18`, color: getCatColor(lead.category) || 'var(--accent)' }}>
                {lead.company.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>{lead.company}</div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {lead.contact && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{lead.contact}</span>}
                  <span style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} />{lead.email}</span>
                  {lead.phone && <span style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={11} />{lead.phone}</span>}
                </div>
              </div>
              <CategoryBadge name={lead.category} color={getCatColor(lead.category)} />
              <div style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                {new Date(lead.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(lead)}><Edit2 size={12} /></button>
                <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(lead)}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal !== null && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal modal-wide">
            <div className="modal-header">
              <h3>{modal === 'add' ? 'Add Lead' : `Edit — ${(modal as Lead).company}`}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}><X size={14} /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label><Building2 size={10} style={{ marginRight: 4 }} />Company Name *</label>
                  <input placeholder="Acme Corp" value={form.company} onChange={fld('company')} autoFocus />
                </div>
                <div className="form-group">
                  <label>Contact Name</label>
                  <input placeholder="John Smith" value={form.contact} onChange={fld('contact')} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label><Mail size={10} style={{ marginRight: 4 }} />Email *</label>
                  <input type="email" placeholder="contact@company.com" value={form.email} onChange={fld('email')} />
                </div>
                <div className="form-group">
                  <label><Phone size={10} style={{ marginRight: 4 }} />Phone</label>
                  <input placeholder="+263 77 123 4567" value={form.phone} onChange={fld('phone')} />
                </div>
              </div>
              <div className="form-group">
                <label><Tag size={10} style={{ marginRight: 4 }} />Category</label>
                <select value={form.category} onChange={fld('category')}>
                  {categories.length === 0 && <option value="">No categories — add in Settings</option>}
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label><FileText size={10} style={{ marginRight: 4 }} />Notes</label>
                <textarea placeholder="Any relevant notes about this lead…" value={form.notes} onChange={fld('notes') as any} style={{ minHeight: 80 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <Spinner size={14} /> : null}
                {modal === 'add' ? 'Add Lead' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {importResult && (
        <div className="modal-overlay" onClick={() => setImportResult(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Complete</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setImportResult(null)}><X size={14} /></button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.2)', borderRadius: 9, padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 700, color: 'var(--accent3)' }}>{importResult.imported}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Imported</div>
                </div>
                <div style={{ flex: 1, background: 'rgba(108,99,255,0.06)', border: '1px solid var(--border)', borderRadius: 9, padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 700, color: 'var(--text2)' }}>{importResult.skipped}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Skipped</div>
                </div>
              </div>
              {importResult.errors.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Skipped rows</div>
                  <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {importResult.errors.map((err, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)', borderRadius: 7, padding: '7px 10px' }}>
                        <AlertCircle size={12} color="var(--accent2)" style={{ flexShrink: 0, marginTop: 1 }} />{err}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {importResult.errors.length === 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--accent3)' }}>
                  <Check size={14} /> All rows imported successfully
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setImportResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Lead"
          message={<>Permanently delete <strong>{deleteTarget.company}</strong>? This cannot be undone.</>}
          onConfirm={confirmDelete} onCancel={() => setDeleteTarget(null)} danger
        />
      )}
    </div>
  );
}